import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  wishContentSchema,
  type ContentBlob,
  type WishContent,
  type WishStatus,
} from "@vmeste/shared";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "../app/session";
import { useMedia, useMediaUrl } from "../lib/media";
import { MediaViewer } from "../components/MediaViewer";
import { useMembers } from "../app/lists";
import { releasePreviewImage, useWishes, type WishItemView } from "../app/wishes";
import { useAttachments, type AttachmentView } from "../app/attachments";
import { useTags, type TagActions, type TagView } from "../app/tags";
import { Sheet } from "../components/Sheet";
import { ScreenHeader } from "../components/ScreenHeader";
import { useChat, type TopicView } from "../app/chat";
import { PANE_HEIGHT, TAB_BAR_MARGIN } from "../app/chrome";
import { AddButton, Button, Card, TextField } from "../components/ui";
import { TagPicker } from "../components/TagPicker";
import { AttachmentStrip } from "../components/AttachmentStrip";
import {
  ChatIcon,
  ClipboardIcon,
  CheckIcon,
  CloseIcon,
  EditIcon,
  LinkIcon,
  PaperclipIcon,
  RefreshIcon,
  TagIcon,
  TrashIcon,
} from "../components/icons";
import { cn } from "../lib/cn";
import { operations } from "../lib/outbox";
import { plaintextOf } from "../lib/plaintext";
import { syncEngine } from "../lib/sync";
import {
  closeDraft,
  minimiseDraft,
  openDraft,
  patchDraft,
  useDraftValues,
} from "../app/draft";
import { useT, type MessageKey } from "../i18n/index.js";

/**
 * Хотелки — their own section (SPEC §4.3).
 *
 * Not a preset of the Lists screen: a wish carries a link, a price and a
 * bought/gifted status, none of which a list item has. Having it in both places
 * was one idea built twice.
 *
 * The link preview comes from the server (CORS makes a client-side unfurl
 * impossible) and arrives after the wish is already on screen. A wish with no
 * preview is a finished wish, not a broken one — half the shops worth wanting
 * something from block scrapers, and the spec says to accept that.
 *
 * The screen is a **card and a sheet**, the shape an event and a task already
 * have. The card answers what the wish is and who is getting it, in about a
 * fifth of the height it used to take; everything else a wish carries — its own
 * words, its tags, its files, its discussion, the preview at a size worth
 * looking at, and editing any of it — is on the sheet a tap away.
 */

const WISH_DRAFT = "wish.new";

const wishReady = (values: { title: string }) => values.title.trim().length > 0;

const STATUS_ORDER: WishStatus[] = ["idea", "bought", "gifted"];

const STATUS_LABEL: Record<WishStatus, MessageKey> = {
  idea: "wishes.statusIdea",
  bought: "wishes.statusBought",
  gifted: "wishes.statusGifted",
};

/**
 * How long the bin stays armed after the first press.
 *
 * Long enough to read the question and press again, short enough that a card
 * scrolled past is not still waiting to delete something when it comes back.
 */
const CONFIRM_TIMEOUT_MS = 4_000;

/* ----------------------------------------------------------------- filters */

/**
 * Three questions the row answers, and they multiply.
 *
 * Deliberately screen state and nothing else: nothing is persisted, nothing is
 * in the URL. A filter here is a way of looking at ten wishes for a moment, not
 * a setting somebody has to remember turning on — coming back to the screen with
 * half the list hidden by a choice made last week is the failure mode this
 * avoids.
 *
 * «Второй участник» is defined as "not me" rather than as a particular person:
 * a space is two people (SPEC §2), and phrasing it that way means the filter
 * still behaves if the other one has not joined yet or is replaced.
 */
type StatusFilter = "all" | WishStatus;

/** Who — me, the other one, or don't care. */
type PersonFilter = "all" | "mine" | "partner";

/** The same, plus the answer only claiming has: nobody took it. */
type ClaimFilter = PersonFilter | "nobody";

interface Filters {
  status: StatusFilter;
  claim: ClaimFilter;
  author: PersonFilter;
  /**
   * A tag id, or `null` for "any".
   *
   * One at a time and not a set: with two people a tag is «подарки» or «на
   * дачу», and two of them at once is a question nobody asks — while the
   * control for it would be twice the size on the screen there is least of.
   */
  tag: string | null;
}

const NO_FILTERS: Filters = { status: "all", claim: "all", author: "all", tag: null };

function isFiltering(filters: Filters): boolean {
  return (
    filters.status !== "all" ||
    filters.claim !== "all" ||
    filters.author !== "all" ||
    filters.tag !== null
  );
}

/** Does this one person's id answer the question the pill asked? */
function personMatches(
  filter: PersonFilter,
  userId: string | null,
  self: string | null,
): boolean {
  if (filter === "all") return true;
  if (!userId) return false;
  // With no session there is no "me", so neither answer can be true — better
  // than quietly treating every wish as somebody else's.
  if (!self) return false;
  return filter === "mine" ? userId === self : userId !== self;
}

/** Every filter is an `and`: the row narrows, it never widens. */
function matchesFilters(
  wish: WishItemView,
  filters: Filters,
  self: string | null,
  tagsOn: (wishId: string) => readonly { id: string }[],
) {
  if (filters.status !== "all" && wish.status !== filters.status) return false;

  if (filters.tag !== null && !tagsOn(wish.id).some((tag) => tag.id === filters.tag)) {
    return false;
  }

  if (filters.claim === "nobody") {
    if (wish.claimedBy !== null) return false;
  } else if (!personMatches(filters.claim, wish.claimedBy, self)) {
    return false;
  }

  return personMatches(filters.author, wish.addedBy, self);
}

/* ------------------------------------------------------------------ switch */

/**
 * One switch, one shape — whatever state the wish is in.
 *
 * These used to be loose ghost buttons, and the row changed its nature with the
 * wish: nothing was claimed, so nothing was filled, and «Возьму на себя /
 * Куплено / Подарено» read as three words floating in the card. Claim it and a
 * pill appeared around one of them — the same three words now looked like a
 * control that had grown a container out of nowhere. Two different objects for
 * one gesture.
 *
 * So the container is always there: a `rounded-pill` track a tonal step above
 * the card, and the segments live in it. The track is what says "these belong
 * together"; the amber-soft segment says which one is on. When nothing is on —
 * a wish already bought, where the segments are only the moves left — the track
 * still reads as one control rather than as scattered text.
 */
/**
 * «Беру на себя» — a switch, drawn as one.
 *
 * It used to share a segmented track with «Куплено» and «Подарено», which said
 * "pick one of three" about three things that are not alternatives: the first
 * turns on and off, and the other two are one-way moves after which the first
 * disappears entirely. Three equal segments on a 390px phone also gave each
 * about 105px, which «Возьму на себя» does not fit — so every unsettled wish
 * carried a two-line control.
 */
const CLAIM =
  "inline-flex min-h-[var(--touch)] shrink-0 items-center gap-1.5 rounded-pill " +
  "px-3.5 text-[0.8125rem] font-medium transition-colors";

const CLAIM_ON = "bg-accent-soft font-semibold text-accent-ink";
const CLAIM_OFF = "bg-surface-alt text-ink-soft hover:text-ink";

/** «Куплено» and «Подарено»: moves, not states, so they are written as verbs
    would be — quiet text, no fill, nothing claiming to be selected. */
const MOVE =
  "inline-flex min-h-[var(--touch)] shrink-0 items-center rounded-pill px-3 " +
  "text-[0.8125rem] font-medium text-muted transition-colors hover:bg-surface-alt hover:text-ink-soft";

/**
 * The track, on the card and on the sheet both.
 *
 * One component rather than two copies, because it is one control: taking a
 * wish on is the same gesture whether it is answered from the list or from the
 * wish itself, and a switch that drifted between the two places would read as
 * two different things.
 */
function StatusSwitch({
  wish,
  mine,
  onClaim,
  onStatus,
}: {
  wish: WishItemView;
  mine: boolean;
  onClaim: () => void;
  onStatus: (status: WishStatus) => void;
}) {
  const t = useT();
  const settled = wish.status !== "idea";

  return (
    // No track around them. A track is what made the row read as "one of
    // three", and it is the reading that was wrong.
    <div className="flex flex-wrap items-center gap-1.5">
      {/* "I'll handle this one" — the point is that two people do not buy the
          same present without knowing. */}
      {!settled ? (
        <button
          type="button"
          onClick={onClaim}
          aria-pressed={mine}
          className={cn(CLAIM, mine ? CLAIM_ON : CLAIM_OFF)}
        >
          {mine ? (
            <CheckIcon
              className="size-[18px] shrink-0"
              strokeWidth={1.9}
              aria-hidden="true"
            />
          ) : null}
          {mine ? t("wishes.claimed") : t("wishes.claim")}
        </button>
      ) : null}

      <span className="flex-1" />

      {STATUS_ORDER.filter((status) => status !== wish.status).map((status) => (
        <button
          key={status}
          type="button"
          onClick={() => onStatus(status)}
          className={MOVE}
        >
          {t(STATUS_LABEL[status])}
        </button>
      ))}
    </div>
  );
}

export function WishesPage() {
  const t = useT();
  const { session } = useSession();
  const spaceId = session?.spaceId ?? null;
  const self = session?.user.id ?? null;

  const { wishes, add, setStatus, toggleClaim, remove, refreshPreview } = useWishes(
    spaceId,
    self,
  );
  const members = useMembers(spaceId);
  // One hook for the screen, not one per row: the tags are the space's, and a
  // row only needs to know which of them are on it.
  const tagging = useTags(spaceId);
  const chat = useChat(spaceId, self, null);
  const navigate = useNavigate();

  /**
   * Open the discussion about a wish, starting it if nobody has.
   *
   * The id is minted before the create leaves the outbox, so the chat screen
   * can be opened on it immediately — the pending overlay has already put the
   * thread in the store by the time the navigation lands.
   */
  function discuss(wish: WishItemView) {
    const existing = chat.discussionFor({ type: "wish", id: wish.id });
    const id = existing?.id ?? chat.createThread(wish.title, { type: "wish", id: wish.id });
    if (id) navigate(`/chat?topic=${id}`);
  }

  const adding = useDraftValues<WishDraft>(WISH_DRAFT);

  /*
   * Arriving from a search hit or the activity feed.
   *
   * Both handed out `/wishes` and nothing more, so finding a wish among forty
   * meant landing at the top of the screen and looking for it again by eye.
   * Read once and dropped from the address: an arrival is not a place, and a
   * reload should not keep re-opening the same card.
   */
  const [params, setParams] = useSearchParams();
  const wanted = params.get("wish");
  const [arrival, setArrival] = useState<string | null>(null);

  useEffect(() => {
    if (!wanted) return;
    setArrival(wanted);
    setParams(new URLSearchParams(), { replace: true });
  }, [wanted, setParams]);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);

  const shown = useMemo(
    () =>
      wishes.filter((wish) =>
        matchesFilters(wish, filters, self, (id) => tagging.tagsOf("wish", id)),
      ),
    [wishes, filters, self, tagging],
  );

  // The other member, by name. A space is two people, so "the one who is not
  // me" is unambiguous; it stays null until they have actually joined, and the
  // pill falls back to wording that names nobody.
  const partnerName =
    (self && members.find((member) => member.user.id !== self)?.user.displayName) ||
    null;

  const open = shown.filter((wish) => wish.status === "idea");
  const settled = shown.filter((wish) => wish.status !== "idea");

  return (
    <div className="flex flex-col gap-4 px-4 pt-4 pb-8">
      <ScreenHeader
        title={t("wishes.title")}
        subtitle={t("wishes.subtitle")}
        /* The form used to sit here open, always — a head-height box on a
           screen where most visits add nothing. Now it is one icon, in the
           place every other screen keeps its own. */
        action={
          <AddButton
              dimmed={adding !== null}
              label={t("wishes.newWish")}
              onClick={() =>
                openDraft<WishDraft>({
                  key: WISH_DRAFT,
                  path: "/wishes",
                  title: t("wishes.newWish"),
                  values: { title: "", url: "", price: "", note: "" },
                  isReady: wishReady,
                  submit: (input) =>
                    add({
                      title: input.title,
                      ...(input.url.trim() ? { url: input.url.trim() } : {}),
                      ...(input.price.trim() ? { price: input.price.trim() } : {}),
                      ...(input.note.trim() ? { note: input.note.trim() } : {}),
                    }),
                })
              }
          />
        }
      />

      {adding ? (
        <Sheet
          title={t("wishes.newWish")}
          onClose={closeDraft}
          onMinimise={minimiseDraft}
        >
          <AddWish
            values={adding}
            onChange={patchDraft}
            onAdd={(input) => {
              closeDraft();
              add({
                title: input.title,
                ...(input.url.trim() ? { url: input.url.trim() } : {}),
                ...(input.price.trim() ? { price: input.price.trim() } : {}),
                ...(input.note.trim() ? { note: input.note.trim() } : {}),
              });
            }}
            onClose={closeDraft}
          />
        </Sheet>
      ) : null}

      {wishes.length === 0 ? (
        <Card>
          <p className="px-4 py-6 text-center text-[0.9375rem] text-muted">
            {t("wishes.empty")}
          </p>
        </Card>
      ) : (
        <FilterBar
          filters={filters}
          tags={tagging.tags}
          partnerName={partnerName}
          shown={shown.length}
          total={wishes.length}
          onChange={setFilters}
        />
      )}

      {/* Filters on, nothing through them: say so, and offer the way back.
          A screen that just goes blank reads as a bug. */}
      {wishes.length > 0 && shown.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center gap-3 px-4 py-6">
            <p className="text-center text-[0.9375rem] text-muted">
              {t("wishes.filterNothing")}
            </p>
            <Button variant="secondary" onClick={() => setFilters(NO_FILTERS)}>
              {t("wishes.filterReset")}
            </Button>
          </div>
        </Card>
      ) : null}

      {open.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {open.map((wish) => (
            <WishRow
              key={wish.id}
              wish={wish}
              spaceId={spaceId}
              self={self}
              tagging={tagging}
              arrived={arrival === wish.id}
              onArrived={() => setArrival(null)}
              authorName={wish.addedBy === self ? t("common.you") : nameOf(members, wish.addedBy)}
              claimerName={nameOf(members, wish.claimedBy)}
              discussion={chat.discussionFor({ type: "wish", id: wish.id })}
              onDiscuss={() => discuss(wish)}
              onStatus={(status) => setStatus(wish.id, status)}
              onClaim={() => toggleClaim(wish.id)}
              onRemove={() => remove(wish.id)}
              onRefreshPreview={() => refreshPreview(wish.id)}
            />
          ))}
        </ul>
      ) : null}

      {settled.length > 0 ? (
        <>
          <h2 className="px-1 pt-2 text-[0.8125rem] font-semibold tracking-wide text-muted uppercase">
            {t("wishes.settled")}
          </h2>
          <ul className="flex flex-col gap-2">
            {settled.map((wish) => (
              <WishRow
                key={wish.id}
                wish={wish}
                spaceId={spaceId}
                self={self}
                tagging={tagging}
                arrived={arrival === wish.id}
                onArrived={() => setArrival(null)}
                authorName={wish.addedBy === self ? t("common.you") : nameOf(members, wish.addedBy)}
              claimerName={nameOf(members, wish.claimedBy)}
                discussion={chat.discussionFor({ type: "wish", id: wish.id })}
                onDiscuss={() => discuss(wish)}
                onStatus={(status) => setStatus(wish.id, status)}
                onClaim={() => toggleClaim(wish.id)}
                onRemove={() => remove(wish.id)}
                onRefreshPreview={() => refreshPreview(wish.id)}
              />
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function nameOf(
  members: ReturnType<typeof useMembers>,
  userId: string | null,
): string | null {
  if (!userId) return null;
  return members.find((member) => member.user.id === userId)?.user.displayName ?? null;
}

/* -------------------------------------------------------------- filter bar */

/** A pill. On is the one amber fill; off is a tonal step, never an outline. */
const PILL =
  "inline-flex min-h-[var(--touch)] shrink-0 items-center rounded-pill px-3.5 " +
  "text-[0.8125rem] transition-colors";

const PILL_ON = "bg-accent-soft font-semibold text-accent-ink";
const PILL_OFF = "bg-surface font-medium text-muted hover:text-ink-soft";

function FilterPill({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(PILL, on ? PILL_ON : PILL_OFF)}
    >
      {children}
    </button>
  );
}

/**
 * One question and its answers.
 *
 * The label is visible rather than only announced, and that is the whole reason
 * the groups are legible: «я» appears twice in the row — once for who took the
 * wish on, once for who wrote it down — and a pill on its own cannot say which
 * question it belongs to. It is `aria-hidden` because the group already carries
 * the same word as its accessible name, and hearing it twice helps nobody.
 */
function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className="flex shrink-0 items-center gap-1">
      <span aria-hidden="true" className="pr-0.5 text-[0.75rem] text-muted">
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * The whole row, on one line that scrolls sideways.
 *
 * Two people and a dozen wishes do not justify a filter panel: eleven pills on
 * one scrolling line cost 44px of height and nothing else, and the count under
 * them appears only once something is actually hidden — «3 из 3» is noise.
 *
 * The negative margin lets the row bleed to the screen edges while the page
 * keeps its padding, so the last pill does not look cut off mid-gutter.
 */
function FilterBar({
  filters,
  tags,
  partnerName,
  shown,
  total,
  onChange,
}: {
  filters: Filters;
  /** Every tag in the space. The group is not drawn when there are none. */
  tags: readonly TagView[];
  partnerName: string | null;
  shown: number;
  total: number;
  onChange: (filters: Filters) => void;
}) {
  const t = useT();
  const partnerLabel = partnerName ?? t("wishes.filterPartner");

  return (
    <div className="flex flex-col gap-1.5">
      <div className="-mx-4 flex gap-4 overflow-x-auto px-4 py-0.5">
        <FilterGroup label={t("wishes.filterStatus")}>
          <FilterPill
            on={filters.status === "all"}
            onClick={() => onChange({ ...filters, status: "all" })}
          >
            {t("wishes.filterAll")}
          </FilterPill>
          {STATUS_ORDER.map((status) => (
            <FilterPill
              key={status}
              on={filters.status === status}
              onClick={() => onChange({ ...filters, status })}
            >
              {t(STATUS_LABEL[status])}
            </FilterPill>
          ))}
        </FilterGroup>

        <FilterGroup label={t("wishes.filterClaim")}>
          <FilterPill
            on={filters.claim === "all"}
            onClick={() => onChange({ ...filters, claim: "all" })}
          >
            {t("wishes.filterAll")}
          </FilterPill>
          <FilterPill
            on={filters.claim === "mine"}
            onClick={() => onChange({ ...filters, claim: "mine" })}
          >
            {t("wishes.filterMine")}
          </FilterPill>
          <FilterPill
            on={filters.claim === "partner"}
            onClick={() => onChange({ ...filters, claim: "partner" })}
          >
            {partnerLabel}
          </FilterPill>
          <FilterPill
            on={filters.claim === "nobody"}
            onClick={() => onChange({ ...filters, claim: "nobody" })}
          >
            {t("wishes.filterUnclaimed")}
          </FilterPill>
        </FilterGroup>

        <FilterGroup label={t("wishes.filterAuthor")}>
          <FilterPill
            on={filters.author === "all"}
            onClick={() => onChange({ ...filters, author: "all" })}
          >
            {t("wishes.filterAll")}
          </FilterPill>
          <FilterPill
            on={filters.author === "mine"}
            onClick={() => onChange({ ...filters, author: "mine" })}
          >
            {t("wishes.filterMine")}
          </FilterPill>
          <FilterPill
            on={filters.author === "partner"}
            onClick={() => onChange({ ...filters, author: "partner" })}
          >
            {partnerLabel}
          </FilterPill>
        </FilterGroup>

        {/*
          Tags could be put on a wish and did nothing afterwards: no filter
          anywhere, and the search does not look at them either. That is work
          with no return — somebody does it three times and then stops. This is
          the return.
        */}
        {tags.length > 0 ? (
          <FilterGroup label={t("wishes.filterTag")}>
            <FilterPill
              on={filters.tag === null}
              onClick={() => onChange({ ...filters, tag: null })}
            >
              {t("wishes.filterAll")}
            </FilterPill>
            {tags.map((tag) => (
              <FilterPill
                key={tag.id}
                on={filters.tag === tag.id}
                onClick={() =>
                  onChange({ ...filters, tag: filters.tag === tag.id ? null : tag.id })
                }
              >
                {tag.name}
              </FilterPill>
            ))}
          </FilterGroup>
        ) : null}
      </div>

      {isFiltering(filters) ? (
        <p role="status" className="px-1 text-[0.75rem] text-muted">
          {t("wishes.filterCount", { count: shown, total })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * «Вставить» beside the link field.
 *
 * A wish usually begins in another app: a link is copied in a browser, the
 * person switches here, and then has to long-press a field and pick «Вставить»
 * out of a system menu that appears somewhere near their thumb. One button is
 * the whole gesture.
 *
 * Drawn only where the clipboard can actually be read — Safari asks for a
 * confirmation each time and Firefox has no such API at all, and a button that
 * does nothing is worse than no button.
 */
function PasteLink({ onPaste }: { onPaste: (url: string) => void }) {
  const t = useT();
  const [failed, setFailed] = useState(false);

  if (failed || typeof navigator === "undefined" || !navigator.clipboard?.readText) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard
          .readText()
          .then((text) => {
            const trimmed = text.trim();
            // Only something that looks like an address. Pasting whatever
            // happened to be copied into a field labelled «Ссылка» is a field
            // that lies about what it holds.
            if (/^https?:\/\//i.test(trimmed)) onPaste(trimmed);
          })
          // Refused, or a browser that asks and was answered no. Once is
          // enough to know the button is not going to work here.
          .catch(() => setFailed(true));
      }}
      className="mb-[1.625rem] grid size-[var(--touch)] shrink-0 place-items-center rounded-pill bg-surface-alt text-ink-soft transition-colors hover:text-ink"
      aria-label={t("wishes.pasteLink")}
      title={t("wishes.pasteLink")}
    >
      <ClipboardIcon className="size-[19px]" strokeWidth={1.8} />
    </button>
  );
}

/**
 * Title first, link second.
 *
 * Pasting a URL is the common case, but a wish with no link is just as valid, so
 * the field that is always required leads.
 *
 * Opened by the «+» in the heading rather than standing here permanently, and
 * therefore whole from the start: the two-step reveal on focus existed because
 * the form was always on screen and had to be small when nobody was using it.
 * A form somebody asked for has no reason to hide half of itself.
 */
function AddWish({
  values,
  onChange,
  onAdd,
  onClose,
}: {
  /** Held outside, so pushing the sheet down keeps it — see `app/draft`. */
  values: WishDraft;
  onChange: (next: WishDraft) => void;
  onAdd: (input: WishDraft) => void;
  onClose: () => void;
}) {
  const t = useT();
  const { title, url } = values;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!wishReady(values)) return;
    onAdd(values);
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 px-4 py-4">
        <TextField
          autoFocus
          label={t("wishes.titleLabel")}
          value={title}
          onChange={(e) => onChange({ ...values, title: e.target.value })}
          placeholder={t("wishes.titlePlaceholder")}
        />
        <div className="flex items-end gap-2">
          <TextField
            label={t("wishes.urlLabel")}
            hint={t("wishes.urlHint")}
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => onChange({ ...values, url: e.target.value })}
            placeholder="https://"
            className="flex-1"
          />
          <PasteLink onPaste={(next) => onChange({ ...values, url: next })} />
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={!title.trim()}>
            {t("common.add")}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
    </form>
  );
}

/** Whitespace and case aside, is the unfurled title saying anything new? */
function saysSomethingElse(previewTitle: string | undefined, own: string): boolean {
  if (!previewTitle) return false;
  const normalise = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");
  return normalise(previewTitle) !== normalise(own);
}

/* ----------------------------------------------------------------- editing */

/**
 * The parts of a wish a person typed themselves.
 *
 * Everything else on one — its status, who took it on, what the server read off
 * the shop's page — is the app's to set, and none of it belongs on a form.
 */
interface WishDraft {
  title: string;
  url: string;
  price: string;
  note: string;
}

function draftFromWish(wish: WishItemView): WishDraft {
  return {
    title: wish.title,
    url: wish.url ?? "",
    price: wish.price ?? "",
    note: wish.note ?? "",
  };
}

/**
 * Decode a wish's content the way `app/wishes` does.
 *
 * The same three lines, because saving has to read what is stored before it can
 * rewrite it, and `none` mode carries plain JSON so no await is needed. If this
 * ever needs a fourth line it belongs in `app/wishes` next to the original.
 */
function readWishContent(blob: ContentBlob | undefined): WishContent | null {
  const value = plaintextOf(blob);
  if (value === undefined) return null;
  const parsed = wishContentSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * Write an edited wish back.
 *
 * Straight to the outbox, exactly as creating one goes — encode with the
 * space's codec, then one `wish.update` carrying the new blob. It is here
 * rather than on `useWishes` for the reason `skipDay` is on the calendar
 * screen: `app/wishes` belongs to another round, and a second mutation path
 * would be a worse answer than one function that uses the same one.
 *
 * Fields are rebuilt rather than merged over the old ones, so clearing the
 * price actually clears it: a spread would only ever add.
 */
async function saveWish(
  spaceId: string,
  wishId: string,
  draft: WishDraft,
): Promise<void> {
  // Asked for before anything is encoded: an encrypted space with no key on
  // this device has nowhere to put the wish, and writing it in the clear is a
  // far worse outcome than a refused save (SPEC §3).
  const codec = syncEngine.codec(spaceId);
  if (!codec) return;

  const title = draft.title.trim();
  if (!title) return;

  const current = readWishContent(syncEngine.view(spaceId).wishes[wishId]?.content);
  if (!current) return;

  const url = draft.url.trim();
  const note = draft.note.trim();
  const price = draft.price.trim();

  const next: WishContent = {
    title,
    ...(url ? { url } : {}),
    ...(note ? { note } : {}),
    ...(price ? { price } : {}),
    // The preview describes the link, not the words around it: it survives a
    // rename and dies with a changed link, because one shop's picture over
    // another shop's wish is worse than no picture at all.
    ...(current.preview && url === current.url ? { preview: current.preview } : {}),
  };

  const content = await codec.encode(next);
  const operation = operations.updateWish(spaceId, wishId, { content });
  syncEngine.mutate(operation);

  // A link that changed took the preview with it, and the picture the preview
  // was showing is now shown by nothing — unless another wish saved the same
  // one, which is what `releasePreviewImage` goes and asks. Not awaited: it
  // waits for the save to reach the server, and the refresh that follows this
  // must not be held up behind a queue that is offline.
  if (current.preview?.image && next.preview?.image !== current.preview.image) {
    void releasePreviewImage(spaceId, current.preview.image, { after: operation.id });
  }
}

/* -------------------------------------------------------------------- card */

/**
 * One wish, as small as it can be and still answer the two questions.
 *
 * Those two are **what it is** and **who is getting it**; everything else the
 * card used to carry — the tag editor, the file strip, the discussion, the
 * 16:9 preview, the note, the refresh — is now on the sheet a tap away. A wish
 * on this screen was four hundred pixels tall on a phone, which meant one and a
 * half of them on a screen, and the tags and files each took a whole line of it
 * to say nothing most of the time.
 *
 * What is left is a 56px picture that *is* the link, a title, one line of
 * counted metadata, the bin, and the switch.
 */
function WishRow({
  wish,
  spaceId,
  self,
  tagging,
  arrived,
  onArrived,
  authorName,
  claimerName,
  discussion,
  onDiscuss,
  onStatus,
  onClaim,
  onRemove,
  onRefreshPreview,
}: {
  wish: WishItemView;
  spaceId: string | null;
  self: string | null;
  tagging: TagActions;
  /** Sent here by a search hit or the activity feed. */
  arrived: boolean;
  onArrived: () => void;
  /** Who added it. Always shown — see the note where it is drawn. */
  authorName: string | null;
  claimerName: string | null;
  discussion: TopicView | null;
  onDiscuss: () => void;
  onStatus: (status: WishStatus) => void;
  onClaim: () => void;
  onRemove: () => void;
  onRefreshPreview: () => void;
}) {
  const t = useT();
  const settled = wish.status !== "idea";
  const mine = wish.claimedBy !== null && wish.claimedBy === self;

  const tags = tagging.tagsOf("wish", wish.id);
  // Only for the count on the card. The strip on the sheet reads the same
  // store, so this costs a selector and no round trip.
  const { attachments, pending } = useAttachments(spaceId, { type: "wish", id: wish.id });
  const files = attachments.length + pending.length;

  const [opened, setOpened] = useState(false);

  /*
   * Somebody was sent to this wish. Open its card and bring it into view — the
   * card is what the search hit was about, not the row it sits on.
   *
   * `onArrived` clears the arrival above, so nothing here has to remember
   * whether it already did this, and a card the person closes stays closed.
   */
  const rowRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (!arrived) return;
    rowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    setOpened(true);
    onArrived();
  }, [arrived, onArrived]);

  /**
   * Deleting takes two presses, the way it already does in chat.
   *
   * A wish is somebody's plan for a present and there is no undo behind the
   * outbox, so a thumb landing on the bin while scrolling must not be the whole
   * story. The button asks instead — and the question goes away by itself, so
   * a card left half-pressed is not still armed when the screen comes back.
   */
  const [confirming, setConfirming] = useState(false);
  const removeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(false), CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [confirming]);

  return (
    <li ref={rowRef}>
      <Card className={cn(settled && "opacity-70")}>
        <div
          className="flex flex-col gap-2 px-3 py-2.5"
          /* Anything else pressed on this card takes the question back. On
             pointerdown and captured, so it lands before the thing being
             pressed acts — the bin itself is excluded, or the second press
             would cancel what it came to confirm. */
          onPointerDownCapture={(event) => {
            if (!confirming) return;
            if (removeRef.current?.contains(event.target as Node)) return;
            setConfirming(false);
          }}
        >
          <div className="flex items-start gap-2.5">
            {/* Keyed by the image so a refreshed preview starts with a clean
                "did this picture load" state instead of inheriting the old one. */}
            <WishLink key={wish.preview?.image ?? "no-image"} wish={wish} spaceId={spaceId} />

            {/* No `aria-label`: the name this button announces is its own
                content, which is the title and then every count under it —
                «плед на диван, вы взяли на себя, 3 тега, 2 файла». A label
                would replace all of that with one word. */}
            <button
              type="button"
              onClick={() => setOpened(true)}
              className={cn(
                "flex min-h-[var(--touch)] min-w-0 flex-1 flex-col justify-center",
                "rounded-row py-0.5 text-left transition-colors hover:bg-surface-alt",
              )}
            >
              {/* Two lines and no more. A marketplace headline pasted as a
                  title is 200 characters, and one of those must not be able to
                  push the switch off the bottom of the screen. */}
              <span
                className={cn(
                  "line-clamp-2 break-words",
                  settled && "line-through decoration-line",
                )}
              >
                {wish.title}
              </span>

              {/* The note, where it was written down and never shown again.
                  Two lines: it is «размер M, синий», not an essay, and one that
                  runs long must not push the switch off the screen. */}
              {wish.note ? (
                <span className="mt-1 line-clamp-2 text-[0.8125rem] break-words text-ink-soft">
                  {wish.note}
                </span>
              ) : null}

              {/* One line for everything the card no longer spells out. It
                  wraps rather than scrolls, but each part is short enough that
                  a second line is the exception and never a third. */}
              <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.75rem] text-muted">
                {claimerName && !settled ? (
                  <span className="rounded-pill bg-accent-soft px-2 py-0.5 font-medium text-accent-ink">
                    {mine
                      ? t("wishes.claimedByYou")
                      : t("wishes.claimedBySomeone", { name: claimerName })}
                  </span>
                ) : null}
                {settled ? <span>{t(STATUS_LABEL[wish.status])}</span> : null}
                {/*
                  Always, and as a bare name.
                  
                  It was shown only for the other person's wishes, on the
                  reasoning that your own name on your own wish says nothing.
                  On a screen that reads as a rule nobody was told: one card
                  carries a line and the next does not, which looks like a row
                  with something missing rather than a row that is yours.
                  
                  A name in the nominative and no preposition, because Russian
                  would decline it and a name does not say how — «от Танечка»
                  was on screen. The same chip the calendar puts on an event
                  for the same question, so the two screens answer «чьё это» the
                  same way.
                */}
                {authorName ? (
                  <span
                    title={t("wishes.addedByWhom", { name: authorName })}
                    className="rounded-pill bg-surface-alt px-2 py-0.5 font-medium text-ink-soft"
                  >
                    {authorName}
                  </span>
                ) : null}
                {wish.host ? (
                  <span className="max-w-full truncate">{wish.host}</span>
                ) : null}
                {wish.price ? (
                  <span className="tabular text-ink-soft">{wish.price}</span>
                ) : null}

                <MetaCount
                  icon={<TagIcon className="size-3.5" strokeWidth={1.8} />}
                  count={tags.length}
                  label={t("wishes.tagCount", { count: tags.length })}
                />
                <MetaCount
                  icon={<PaperclipIcon className="size-3.5" strokeWidth={1.8} />}
                  count={files}
                  label={t("wishes.fileCount", { count: files })}
                />
                <MetaCount
                  icon={<ChatIcon className="size-3.5" strokeWidth={1.8} />}
                  count={discussion?.messageCount ?? 0}
                  label={t("wishes.discussionCount", {
                    count: discussion?.messageCount ?? 0,
                  })}
                />

                {/* Announced by the sheet, not here: a live region inside
                    eleven cards would talk over the list. */}
                {wish.unfurling ? (
                  <RefreshIcon className="size-3.5 animate-spin" aria-hidden="true" />
                ) : null}
              </span>
            </button>

            {/* Straight into the conversation about this one.
                
                The count for it was already on the card, inside the button
                that opens the sheet — so the way to «поговорить об этом» was
                open the wish, find «Обсудить», and go. Two taps for the thing
                a shared wishlist is for. Amber once something has been said,
                because then it is a place to go back to rather than a place to
                start. */}
            <button
              type="button"
              onClick={onDiscuss}
              aria-label={t("wishes.discussAbout", { title: wish.title })}
              className={cn(
                "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill transition-colors",
                (discussion?.messageCount ?? 0) > 0
                  ? "text-accent hover:bg-accent-soft"
                  : "text-muted/60 hover:bg-surface-alt hover:text-ink",
              )}
            >
              <ChatIcon className="size-[18px]" strokeWidth={1.8} />
            </button>

            {/* Asked, the bin grows its words — the only `neg` fill on the
                card, and the only place it is earned. */}
            <button
              ref={removeRef}
              type="button"
              onClick={() => (confirming ? onRemove() : setConfirming(true))}
              aria-label={
                confirming
                  ? t("wishes.removeConfirm", { title: wish.title })
                  : t("wishes.remove", { title: wish.title })
              }
              className={cn(
                "inline-flex min-h-[var(--touch)] shrink-0 items-center justify-center gap-1.5",
                "rounded-pill transition-colors",
                confirming
                  ? "bg-neg-soft px-3 text-[0.8125rem] font-semibold text-neg"
                  : "size-[var(--touch)] text-muted hover:bg-surface-alt hover:text-neg",
              )}
            >
              <TrashIcon
                className="size-[18px] shrink-0"
                strokeWidth={1.8}
                aria-hidden="true"
              />
              {confirming ? t("common.confirmDelete") : null}
            </button>
          </div>

          {/* The track. The one control the card keeps, because «кто забрал» is
              half of what this screen is for and it has to be answerable
              without opening anything. */}
          <StatusSwitch
            wish={wish}
            mine={mine}
            onClaim={onClaim}
            onStatus={onStatus}
          />
        </div>
      </Card>

      {opened ? (
        <WishSheet
          wish={wish}
          spaceId={spaceId}
          tagging={tagging}
          discussion={discussion}
          mine={mine}
          onClaim={onClaim}
          onStatus={onStatus}
          onDiscuss={() => {
            setOpened(false);
            onDiscuss();
          }}
          onRefreshPreview={onRefreshPreview}
          onSave={(draft) => {
            if (!spaceId) return;
            // A link that changed means the picture on file describes the
            // wrong page; asking the server again is the same path the wish
            // took when it was created.
            const nextUrl = draft.url.trim();
            const changed = nextUrl !== (wish.url ?? "");
            void saveWish(spaceId, wish.id, draft).then(() => {
              if (changed && nextUrl) onRefreshPreview();
            });
          }}
          onClose={() => setOpened(false)}
        />
      ) : null}
    </li>
  );
}

/**
 * An icon and a number, standing in for a list the card no longer prints.
 *
 * The word is the accessible name rather than the text: «3 тега» read out is
 * exactly right, and «3 тега» written out three times across one line is the
 * row of chips this replaced.
 */
function MetaCount({
  icon,
  count,
  label,
}: {
  icon: ReactNode;
  count: number;
  label: string;
}) {
  if (count === 0) return null;

  return (
    <span className="inline-flex items-center gap-1" title={label} aria-label={label}>
      <span aria-hidden="true" className="inline-flex">
        {icon}
      </span>
      <span aria-hidden="true" className="tabular">
        {count}
      </span>
    </span>
  );
}

/**
 * The picture, and it opens the shop.
 *
 * A **sibling** of the button that opens the sheet, never a child of it. An
 * `<a>` inside a `<button>` is invalid HTML, and the browsers that render it
 * anyway disagree about which of the two a tap belongs to — so the fix is not a
 * `stopPropagation` on a nested link, it is not nesting one. Side by side, each
 * target owns its own pixels and no event has to be stopped from travelling.
 *
 * With no picture it is the link glyph on the same square, so the row keeps one
 * shape and the gesture is in the same place on every wish that has a link. A
 * wish with no link at all gets the square only if there is something to show
 * in it.
 */
function WishLink({ wish, spaceId }: { wish: WishItemView; spaceId: string | null }) {
  const t = useT();
  const [broken, setBroken] = useState(false);

  /*
   * Through the media hook, not straight into `src`.
   *
   * A preview's picture is a file the space owns, so the catch-up pass seals it
   * along with everything else — after which the bytes behind this path are an
   * envelope, and an `<img>` pointed at them shows a broken picture. In a
   * plaintext space the hook hands the same path straight back.
   */
  const resolved = useMediaUrl(spaceId, wish.preview?.image ?? "");
  const image = broken || !wish.preview?.image ? undefined : (resolved ?? undefined);
  const box =
    "grid size-14 shrink-0 place-items-center overflow-hidden rounded-row bg-surface-alt";

  if (!wish.url) {
    if (!image) return null;
    return (
      <span className={box}>
        <img
          src={image}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className="size-full object-cover"
        />
      </span>
    );
  }

  return (
    <a
      href={wish.url}
      target="_blank"
      // noopener rides along with noreferrer: the opened page must not get a
      // handle on this window, and the wish list has no business in their logs.
      rel="noreferrer"
      title={wish.url}
      aria-label={t("wishes.openLink", { host: wish.host ?? wish.url })}
      className={cn(box, "relative text-muted transition-colors hover:text-accent")}
    >
      {image ? (
        <>
          <img
            src={image}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setBroken(true)}
            className="size-full object-cover"
          />
          {/* Over the picture, because a picture on a card does not otherwise
              look like something that goes anywhere — which is exactly the
              complaint this answers. */}
          <span
            aria-hidden="true"
            className="absolute right-0.5 bottom-0.5 grid size-5 place-items-center rounded-pill bg-surface/90 text-muted"
          >
            <LinkIcon className="size-3" strokeWidth={2} />
          </span>
        </>
      ) : (
        <LinkIcon className="size-5" strokeWidth={1.8} />
      )}
    </a>
  );
}

/**
 * One picture in the wish's grid, and the viewer it opens.
 *
 * The mime and the url come from `useMedia` rather than from the row: in an
 * encrypted space the row calls every file `application/octet-stream`, and the
 * real type only comes back out of the envelope.
 */
function WishImage({ file }: { file: AttachmentView }) {
  const t = useT();
  const { session } = useSession();
  const spaceId = session?.spaceId ?? null;
  const media = useMedia(spaceId, file.url);
  const [open, setOpen] = useState(false);
  const tile = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={tile}
        type="button"
        onClick={() => setOpen(true)}
        title={file.name}
        // The name is the button's, not the picture's: with `alt` on both a
        // screen reader reads the filename twice per file.
        aria-label={t("attachments.open", { name: file.name })}
        className="block aspect-square w-full overflow-hidden rounded-row bg-surface-alt transition-opacity hover:opacity-90"
      >
        {media.url ? (
          <img
            src={media.url}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
        ) : null}
      </button>

      {open && media.url ? (
        <MediaViewer
          url={media.url}
          name={file.name}
          mime={media.mime ?? file.mime}
          returnFocusTo={tile}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------- sheet */

/** The two glyph buttons in the sheet's header — ✕ and the pencil. */
const SHEET_ICON =
  "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted " +
  "transition-colors hover:bg-surface-alt hover:text-ink";

/**
 * Everything a wish is, on a sheet that slides up over the screen.
 *
 * The same shape an event and a task already open into, for the same reasons:
 * the card is a line in a list and a phone has one screenful of them, so what a
 * wish carries besides its name — the words somebody wrote about it, the tags,
 * the files, the discussion, the preview at a size worth looking at — belongs
 * over the list rather than inside it.
 *
 * It has **two states, not two windows**. Reading a wish and rewriting one are
 * different jobs: opening it used to land straight in a form, so the answer to
 * «сколько это стоит» was a text box with the price already typed in it, and
 * the note somebody wrote read as something waiting to be filled in. So the
 * sheet opens on the wish as it is — the picture, the words, the tags, the
 * files, who is getting it — and the pencil turns the same sheet into the form.
 * A second sheet over the first would have been a stack to dig back out of.
 *
 * Its ceiling and its bottom padding come from `app/chrome`, so it stops short
 * of the floating tab bar rather than of a number typed here.
 */
function WishSheet({
  wish,
  spaceId,
  tagging,
  discussion,
  mine,
  onClaim,
  onStatus,
  onDiscuss,
  onRefreshPreview,
  onSave,
  onClose,
}: {
  wish: WishItemView;
  spaceId: string | null;
  tagging: TagActions;
  discussion: TopicView | null;
  mine: boolean;
  onClaim: () => void;
  onStatus: (status: WishStatus) => void;
  onDiscuss: () => void;
  onRefreshPreview: () => void;
  onSave: (draft: WishDraft) => void;
  onClose: () => void;
}) {
  const t = useT();

  /*
   * The draft *is* the edit state: a wish being rewritten has one, a wish being
   * read has none. One piece of state rather than a mode flag beside a draft,
   * so the two can never disagree — and because it is minted from the wish each
   * time the pencil is pressed, «Отмена» needs to undo nothing, and a half-typed
   * title cannot outlive the sheet it was typed on.
   *
   * Nothing is written back until «Готово». A wish's link is the one field with
   * a consequence behind it — a new one throws the preview away and asks the
   * server for another — so committing it halfway through being typed would
   * refetch a page nobody meant.
   */
  const [draft, setDraft] = useState<WishDraft | null>(null);
  const editing = draft !== null;

  // The form is in the scrolling body and «Готово» is in the header that does
  // not scroll, so the button is tied to the form by name rather than by nesting.
  const formId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);

  // Read through a ref so the effect below can be set up once, on the way in:
  // re-running it would pull focus back out of whichever field the person had
  // just moved into. Escape means "back one step", so it is the pencil that
  // decides what that step is — the form gives way to the wish, the wish gives
  // way to the screen.
  const escapeRef = useRef<() => void>(onClose);
  escapeRef.current = editing ? () => setDraft(null) : onClose;

  useEffect(() => {
    // Focus lands on the sheet itself rather than on the first field: on a
    // phone, focusing a text box also throws the keyboard up over half of what
    // was just opened. Re-run when the state changes, because the button that
    // changed it — the pencil, «Отмена» — is gone by then, and focus would
    // otherwise fall out of the dialog onto the page behind it.
    sheetRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== "Escape") return;
      keyEvent.preventDefault();
      escapeRef.current();
    };

    // The page underneath stays where it was rather than scrolling away under
    // the sheet.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  /** Save, and land back on the wish rather than out of it. */
  function commit(next: WishDraft) {
    if (!next.title.trim()) return;
    onSave(next);
    setDraft(null);
  }

  return createPortal(
    <>
      {/* Black rather than a token: dimming has to read as shade in both
          themes, and every surface token flips between them. */}
      <div onClick={onClose} aria-hidden="true" className="fixed inset-0 z-40 bg-black/45" />

      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("wishes.detailsOf", { title: wish.title })}
        tabIndex={-1}
        className={cn(
          "pad-safe-x fixed inset-x-0 z-40 mx-auto w-full max-w-2xl",
          "flex flex-col overflow-hidden rounded-t-card bg-surface shadow-soft outline-none",
        )}
        style={{
          /*
           * Above the keyboard, not under it.
           *
           * `bottom-0` is the bottom of the *layout* viewport, and on iOS that
           * does not move when a keyboard comes up — the visual viewport
           * shrinks instead, so a sheet pinned to the bottom had its last
           * fields and its buttons behind the keys. `--keyboard-inset` is that
           * measurement (see `app/keyboard`), and it is nought on a laptop and
           * on Android, where the layout viewport shrinks by itself.
           */
          bottom: "var(--keyboard-inset, 0px)",
          maxHeight: PANE_HEIGHT,
          paddingBottom: `calc(var(--safe-bottom) + ${TAB_BAR_MARGIN}px)`,
        }}
      >
        <header className="flex items-center gap-2 border-b border-line-soft px-3 py-2.5">
          {/* Closing is on the left in both states. The button on the right is
              the one that changes — pencil, then «Готово» — and a ✕ that swapped
              sides with it would be a second thing to look for. */}
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className={SHEET_ICON}
          >
            <CloseIcon className="size-[20px]" strokeWidth={1.9} />
          </button>

          <h2 className="min-w-0 flex-1 truncate font-display text-[1.0625rem]">
            {editing ? t("wishes.editTitle") : t("wishes.details")}
          </h2>

          {editing ? (
            /* Outside the form it submits, and tied to it by `form`: the one
               commit path is the form's own, so a keyboard's «Enter» in a field
               does what the button does.
               
               `key` is load-bearing, and this is the bug it fixes. Without it
               React sees a `<button>` in the same slot and reuses the DOM node,
               changing its attributes in place. The state flush happens inside
               the click handler, so by the time the browser runs that same
               click's activation behaviour the node it pressed is a submit
               button tied to this form — and the browser submits it. Pressing
               the pencil opened the form and saved it again in one gesture,
               about eight milliseconds apart, which on screen is nothing
               happening at all. Two keys, two nodes, no in-place mutation. */
            <button
              key="commit"
              type="submit"
              form={formId}
              disabled={!draft.title.trim()}
              className={cn(
                "inline-flex min-h-[var(--touch)] shrink-0 items-center rounded-pill px-3",
                "text-[0.9375rem] font-semibold text-accent transition-colors",
                "hover:bg-surface-alt disabled:text-muted disabled:hover:bg-transparent",
              )}
            >
              {t("wishes.done")}
            </button>
          ) : (
            <button
              key="edit"
              type="button"
              onClick={() => setDraft(draftFromWish(wish))}
              aria-label={t("wishes.edit")}
              title={t("wishes.edit")}
              className={SHEET_ICON}
            >
              <EditIcon className="size-[19px]" strokeWidth={1.9} />
            </button>
          )}
        </header>

        {/* `min-h-0` so this is the part that shrinks and scrolls when the
            sheet hits its ceiling — without it a flex child refuses to go below
            its content height and the buttons are simply cut off. */}
        <div className="flex min-h-0 flex-col overflow-x-hidden overflow-y-auto overscroll-contain">
          {editing ? (
            <WishForm
              id={formId}
              draft={draft}
              onChange={setDraft}
              onSubmit={commit}
              onCancel={() => setDraft(null)}
            >
              {/* Files live on the wish itself, not in a thread about it: a
                  photo of the thing, a receipt, a screenshot of the size chart.
                  Attaching and removing are changes to the wish, so they are
                  behind the pencil; reading them is not, so the sheet shows them
                  as pictures on the way in. */}
              <AttachmentStrip
                spaceId={spaceId}
                target={{ type: "wish", id: wish.id }}
                compact
              />
            </WishForm>
          ) : (
            <WishDetails
              wish={wish}
              spaceId={spaceId}
              tagging={tagging}
              discussion={discussion}
              mine={mine}
              onClaim={onClaim}
              onStatus={onStatus}
              onDiscuss={onDiscuss}
              onRefreshPreview={onRefreshPreview}
            />
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

/* -------------------------------------------------------------------- view */

/** A quiet row-shaped action — «Обновить превью», «Обсудить». */
const SHEET_ACTION =
  "inline-flex min-h-[var(--touch)] w-fit items-center gap-1.5 rounded-pill px-2 " +
  "text-[0.8125rem] text-muted transition-colors hover:text-accent";

/**
 * The wish, read rather than filled in.
 *
 * Nothing here is a text box, and that is the whole point: a price is a price,
 * not a price already typed into a field, and a wish with no note shows no note
 * instead of an empty box waiting for one.
 *
 * Two things that look like editing stay anyway, because neither is a change to
 * the wish. The switch answers «кто берёт это на себя» — the question the whole
 * screen exists for, and a person answering it has not come to rewrite anything.
 * The tags are chips until they are tapped, so they read as what the wish is
 * filed under rather than as a control.
 */
function WishDetails({
  wish,
  spaceId,
  tagging,
  discussion,
  mine,
  onClaim,
  onStatus,
  onDiscuss,
  onRefreshPreview,
}: {
  wish: WishItemView;
  spaceId: string | null;
  tagging: TagActions;
  discussion: TopicView | null;
  mine: boolean;
  onClaim: () => void;
  onStatus: (status: WishStatus) => void;
  onDiscuss: () => void;
  onRefreshPreview: () => void;
}) {
  const t = useT();

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* The preview at the size it was drawn for, and the whole block opens
          the shop — nothing wraps it here, so there is no button to nest a link
          inside. Keyed by the image so a refreshed preview starts with a clean
          "did this picture load" state instead of inheriting the old one. */}
      <WishPreviewCard
        key={wish.preview?.image ?? "no-image"}
        wish={wish}
        spaceId={spaceId}
      />

      {/* The address in words, under the picture that cannot say it.
          `rel="noreferrer"`: the destination has no business learning which
          page sent somebody, and `noopener` comes with it. */}
      {wish.url ? (
        <a
          href={wish.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-[var(--touch)] w-fit max-w-full items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] text-accent transition-colors hover:text-accent-ink"
        >
          <LinkIcon className="size-[18px] shrink-0" strokeWidth={1.9} />
          <span className="truncate">{wish.url}</span>
        </a>
      ) : null}

      {wish.unfurling ? (
        <p
          role="status"
          className="flex min-h-[var(--touch)] items-center gap-1.5 px-2 text-[0.8125rem] text-muted"
        >
          <RefreshIcon className="size-[18px] animate-spin" aria-hidden="true" />
          {t("wishes.unfurling")}
        </p>
      ) : wish.url ? (
        // Retry, because "the site was slow / blocked us this time" is the
        // common failure and asking again is often all it takes.
        <button type="button" onClick={onRefreshPreview} className={SHEET_ACTION}>
          <RefreshIcon className="size-[18px]" strokeWidth={1.9} aria-hidden="true" />
          {t("wishes.refreshPreview")}
        </button>
      ) : null}

      {/* The wish in its own words. `break-words`, because a pasted
          marketplace headline is one 200-character word as often as not. */}
      <div className="flex flex-col gap-1.5 px-1">
        <h3 className="font-display text-[1.25rem] leading-snug break-words">
          {wish.title}
        </h3>
        {wish.price ? (
          <p className="tabular text-[0.9375rem] text-ink-soft">{wish.price}</p>
        ) : null}
        {wish.note ? (
          // The line breaks somebody typed are part of what they wrote — a size
          // on one line and a colour on the next is a list, not a paragraph.
          <p className="text-[0.9375rem] whitespace-pre-wrap break-words text-muted">
            {wish.note}
          </p>
        ) : null}
      </div>

      <StatusSwitch wish={wish} mine={mine} onClaim={onClaim} onStatus={onStatus} />

      {/* Tags are read on the card as a number and edited here — «подарки»,
          «на дачу» — so the same word means the same thing on a wish, an item
          and an event (SPEC §4.6). */}
      <TagPicker
        selected={tagging.tagsOf("wish", wish.id)}
        available={tagging.tags}
        onLink={(tagId) => tagging.link(tagId, "wish", wish.id)}
        onUnlink={(tagId) => tagging.unlink(tagId, "wish", wish.id)}
        onCreate={(name, color) => tagging.createOn(name, "wish", wish.id, color)}
        label={wish.title}
      />

      <WishFiles spaceId={spaceId} wishId={wish.id} />

      {/* The discussion is a chat thread that knows what it is about, so this
          goes there rather than growing a second message list here. Its title
          is the wish's, which is what makes the thread readable from the chat
          side too. */}
      <button type="button" onClick={onDiscuss} className={SHEET_ACTION}>
        <ChatIcon className="size-[18px]" strokeWidth={1.9} />
        {discussion
          ? t("wishes.discussionCount", { count: discussion.messageCount })
          : t("wishes.discuss")}
      </button>
    </div>
  );
}

/**
 * The files, as the files — not as their names.
 *
 * A photo of the thing, a screenshot of the size chart and a receipt are all
 * pictures, and a picture answers «что это» in the time it takes to look at it,
 * where `IMG_20240817_113045.jpg` answers nothing at all. So the images are a
 * grid of squares and the grid is dense: four to a phone's width, cropped
 * square, which fits eight files in the height one 84px strip used and leaves
 * the switch and the discussion on the screen underneath them.
 *
 * A PDF has no picture to show, so it keeps the row with its name — the only
 * useful thing about it. Tapping either opens the file at full size in a new
 * tab, which is the browser's own viewer: it pinch-zooms a photo and paginates
 * a PDF better than anything worth writing here.
 *
 * Read-only on purpose. Attaching and removing are on the same strip they
 * always were, one press of the pencil away — this is the sheet's *view*, and
 * a dashed «+» tile among the photos would put a control back in it.
 */
function WishFiles({ spaceId, wishId }: { spaceId: string | null; wishId: string }) {
  const t = useT();
  const { attachments, pending } = useAttachments(spaceId, { type: "wish", id: wishId });

  if (attachments.length === 0 && pending.length === 0) return null;

  const images = attachments.filter((file) => file.isImage);
  const others = attachments.filter((file) => !file.isImage);
  const squares = images.length > 0 || pending.length > 0;

  return (
    <div className="flex flex-col gap-1.5">
      {squares ? (
        <ul role="list" className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
          {/* The same viewer the chat opens, not a link out to the browser.
              A picture behaving one way here and another way there is the kind
              of difference nobody can explain and everybody notices. */}
          {images.map((file) => (
            <li key={file.id}>
              <WishImage file={file} />
            </li>
          ))}

          {/* An upload started under the pencil and still going when «Готово»
              was pressed. Without a square for it the file would simply not be
              there, and a failed one would have vanished without a word. */}
          {pending.map((row) =>
            row.error ? (
              <li
                key={row.id}
                className="grid aspect-square place-items-center rounded-row bg-neg-soft px-1.5 text-center"
              >
                <span className="line-clamp-3 text-[0.625rem] leading-tight text-neg">
                  {row.error}
                </span>
              </li>
            ) : (
              <li
                key={row.id}
                aria-label={t("attachments.uploading", {
                  name: row.name,
                  percent: Math.round(row.progress * 100),
                })}
                className="tabular grid aspect-square place-items-center rounded-row bg-surface-alt text-[0.8125rem] text-muted"
              >
                {Math.round(row.progress * 100)}%
              </li>
            ),
          )}
        </ul>
      ) : null}

      {others.map((file) => (
        <a
          key={file.id}
          href={file.url}
          target="_blank"
          rel="noreferrer"
          title={file.name}
          aria-label={t("attachments.open", { name: file.name })}
          className="flex min-h-[var(--touch)] items-center gap-2 rounded-row bg-surface-alt px-3 text-[0.8125rem] text-ink-soft transition-opacity hover:opacity-90"
        >
          <PaperclipIcon
            className="size-[18px] shrink-0 text-muted"
            strokeWidth={1.8}
            aria-hidden="true"
          />
          <span className="truncate">{file.name}</span>
        </a>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------- edit */

/**
 * The four fields a person filled in, back in front of them.
 *
 * Nothing here is new information: it is the add form with the two fields it
 * hides until they are wanted, which is why the wording is shared with it. The
 * same four fields it has always had — the pencil changed when this is on the
 * screen, not what is on it.
 */
function WishForm({
  id,
  draft,
  onChange,
  onSubmit,
  onCancel,
  children,
}: {
  /** Named, so the «Готово» up in the header can submit it. */
  id: string;
  draft: WishDraft;
  onChange: (draft: WishDraft) => void;
  onSubmit: (draft: WishDraft) => void;
  onCancel: () => void;
  /**
   * What belongs to the wish but not to the draft — its files. They act the
   * moment they are pressed, so they sit above «Отмена» rather than below it,
   * and nothing under that button is waiting to be saved.
   */
  children?: ReactNode;
}) {
  const t = useT();
  const noteId = useId();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!draft.title.trim()) return;
    onSubmit(draft);
  }

  const set = (patch: Partial<WishDraft>) => onChange({ ...draft, ...patch });

  return (
    <form id={id} onSubmit={submit} className="flex flex-col gap-3 p-4">
      <TextField
        label={t("wishes.fieldTitle")}
        value={draft.title}
        onChange={(e) => set({ title: e.target.value })}
        placeholder={t("wishes.titlePlaceholder")}
      />

      <TextField
        label={t("wishes.urlLabel")}
        hint={t("wishes.urlHint")}
        type="url"
        inputMode="url"
        value={draft.url}
        onChange={(e) => set({ url: e.target.value })}
        placeholder="https://"
      />

      <TextField
        label={t("wishes.fieldPrice")}
        hint={t("common.optional")}
        value={draft.price}
        onChange={(e) => set({ price: e.target.value })}
        placeholder={t("wishes.pricePlaceholder")}
      />

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={noteId}
          className="px-1 text-[0.8125rem] font-medium text-ink-soft"
        >
          {t("wishes.fieldNote")}
          <span className="ml-1.5 font-normal text-muted">{t("common.optional")}</span>
        </label>
        <textarea
          id={noteId}
          value={draft.note}
          onChange={(e) => set({ note: e.target.value })}
          rows={2}
          placeholder={t("wishes.notePlaceholder")}
          className={cn(
            // The same filled field as `TextField`, which has no textarea of
            // its own — one tonal step off the surface and not a line in sight.
            "min-h-[68px] w-full resize-y rounded-row bg-surface-alt px-4 py-2.5",
            "outline-none placeholder:text-muted",
          )}
        />
      </div>

      {children}

      {/* The way out of the form, and only that: it hands the wish back the way
          it was written down, which is why the draft is minted fresh every time
          the pencil is pressed. Saving is «Готово» in the header, where it stays
          in reach while the fields scroll under it. */}
      <Button type="button" variant="ghost" onClick={onCancel} className="w-fit">
        {t("common.cancel")}
      </Button>
    </form>
  );
}

/**
 * What the server managed to read off the page.
 *
 * Everything in it is optional and nothing here invents a stand-in: a preview
 * with only a site name renders as a site name. The image box holds its
 * aspect ratio from the first paint, so a slow picture never shoves the buttons
 * down the screen under somebody's thumb.
 *
 * The whole block is the link when there is one. It lives on the sheet and
 * nowhere else, so there is no enclosing button to nest an `<a>` inside — the
 * card's own picture is a link for the same reason, by standing beside the
 * button rather than inside it.
 */
function WishPreviewCard({
  wish,
  spaceId,
}: {
  wish: WishItemView;
  spaceId: string | null;
}) {
  const t = useT();
  const [imageBroken, setImageBroken] = useState(false);
  // Asked for before the early return: a hook cannot be called conditionally,
  // and a wish with no preview simply asks about nothing.
  const image = useMediaUrl(spaceId, wish.preview?.image ?? "");
  const preview = wish.preview;
  if (!preview) return null;

  const altTitle = saysSomethingElse(preview.title, wish.title) ? preview.title : null;
  const site = preview.siteName ?? wish.host;
  // The price is not in here any more: this block is what the *shop's page*
  // says, and the price is what a person typed. The sheet prints it under the
  // title now, so having it in both places was the same number twice.
  const hasText = Boolean(altTitle || preview.description || site);

  if (!preview.image && !hasText) return null;

  const body = (
    <>
      {preview.image ? (
        <div className="aspect-[16/9] w-full bg-surface-alt">
          {imageBroken || !image ? (
            <div className="grid size-full place-items-center px-3 text-center text-[0.75rem] text-muted">
              {t("wishes.imageBroken")}
            </div>
          ) : (
            <img
              src={image}
              // Decorative: the wish's own title is right above it, and reading
              // a shop's headline aloud twice helps nobody.
              alt=""
              loading="lazy"
              decoding="async"
              onError={() => setImageBroken(true)}
              className="size-full object-cover"
            />
          )}
        </div>
      ) : null}

      {hasText ? (
        <div className="flex flex-col gap-1 px-3 py-2">
          {altTitle ? (
            <p className="line-clamp-2 text-[0.875rem] break-words text-ink-soft">
              {altTitle}
            </p>
          ) : null}
          {preview.description ? (
            <p className="line-clamp-2 text-[0.8125rem] break-words text-muted">
              {preview.description}
            </p>
          ) : null}
          {site ? <p className="text-[0.75rem] text-muted">{site}</p> : null}
        </div>
      ) : null}
    </>
  );

  // A block inside a card, so: one tonal step up from the card and no line
  // around it. `rounded-row`, because it is something in a card and not a card
  // of its own — `overflow-hidden` hands the same corner to the image.
  const box = "block overflow-hidden rounded-row bg-surface-alt";

  return wish.url ? (
    <a
      href={wish.url}
      target="_blank"
      rel="noreferrer"
      aria-label={t("wishes.openLink", { host: wish.host ?? wish.url })}
      className={cn(box, "transition-opacity hover:opacity-90")}
    >
      {body}
    </a>
  ) : (
    <div className={box}>{body}</div>
  );
}
